// CutoutTouchUp — manual alpha brush for cleaning up imperfect ML cutouts.
//
// Two brushes:
//   ➕ Add  — paint the area back IN. RGB sourced from the original photo so
//             restored regions look authentic (not painted black).
//   ➖ Erase — paint the area OUT. Alpha → 0.
//
// Touch + mouse. Brush size + opacity sliders. Undo stack (per-stroke).
// Confirm returns a PNG blob via onConfirm.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const B = {
  navy:  "#0d1833",
  cream: "#ffdaad",
  pink:  "#ef537e",
  gold:  "#fcb31f",
  sky:   "#4aa4e1",
  green: "#70be44",
  red:   "#c94444",
  border: "rgba(255,218,173,0.22)",
  muted:  "rgba(255,218,173,0.6)",
};

const MAX_DISPLAY_DIM = 1280; // capped for memory; exported at native cutout dims

export default function CutoutTouchUp({ cutoutSrc, originalSrc, onConfirm, onCancel }) {
  const [tool, setTool]       = useState("add"); // "add" | "erase"
  const [size, setSize]       = useState(18);     // brush radius in DISPLAY px (smaller default — Dan flagged 40 as too big)
  const [hardness, setHardness] = useState(0.85); // 0–1: 1=hard edge, 0=very soft
  const [zoom,    setZoom]    = useState(1);
  const [pan,     setPan]     = useState({ x: 0, y: 0 }); // px offset of canvas inside its wrapper
  const [panMode, setPanMode] = useState(false);  // when true, single-touch drag = pan instead of paint
  const [spaceHeld, setSpaceHeld] = useState(false); // space-to-pan, desktop convenience
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [history, setHistory] = useState([]); // ImageData snapshots (for undo)
  const [error,   setError]   = useState(null);
  const [dimMismatch, setDimMismatch] = useState(false); // cutout dims ≠ original dims

  const wrapperRef = useRef(null);
  const canvasRef  = useRef(null);
  const ctxRef     = useRef(null);
  const cutoutImgRef   = useRef(null); // HTMLImageElement of cutout
  const originalImgRef = useRef(null); // HTMLImageElement of original
  const dimsRef    = useRef({ w: 0, h: 0, displayW: 0, displayH: 0 });
  const drawingRef = useRef(false);
  const lastPtRef  = useRef(null);

  // Load both images then draw cutout into canvas
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cutout, original] = await Promise.all([
          loadImage(cutoutSrc),
          loadImage(originalSrc),
        ]);
        if (cancelled) return;
        cutoutImgRef.current   = cutout;
        originalImgRef.current = original;

        const w = cutout.naturalWidth;
        const h = cutout.naturalHeight;
        // Detect legacy trimmed cutouts — Add tool can't align without offsets.
        if (original.naturalWidth !== w || original.naturalHeight !== h) {
          setDimMismatch(true);
          // Auto-switch to erase since add will misalign
          setTool("erase");
        }
        // Scale display down to fit MAX_DISPLAY_DIM and viewport
        const maxW = Math.min(window.innerWidth - 40, MAX_DISPLAY_DIM);
        const maxH = window.innerHeight - 240;
        const scale = Math.min(1, maxW / w, maxH / h);
        const displayW = Math.round(w * scale);
        const displayH = Math.round(h * scale);
        dimsRef.current = { w, h, displayW, displayH, scale };

        const canvas = canvasRef.current;
        canvas.width  = w;
        canvas.height = h;
        canvas.style.width  = `${displayW}px`;
        canvas.style.height = `${displayH}px`;
        const ctx = canvas.getContext("2d");
        ctxRef.current = ctx;
        ctx.drawImage(cutout, 0, 0, w, h);
        // Snapshot initial state for undo
        setHistory([ctx.getImageData(0, 0, w, h)]);
        setLoading(false);
      } catch (err) {
        console.error("CutoutTouchUp load failed", err);
        if (!cancelled) {
          setError("Couldn't load the images.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cutoutSrc, originalSrc]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
      else if ((e.metaKey || e.ctrlKey) && e.key === "z") { e.preventDefault(); undo(); }
      else if (e.key === "[") setSize((s) => Math.max(2, s - 4));
      else if (e.key === "]") setSize((s) => Math.min(200, s + 4));
      else if (e.key === "a" || e.key === "A") setTool("add");
      else if (e.key === "e" || e.key === "E") setTool("erase");
      else if (e.key === " ") { e.preventDefault(); setSpaceHeld(true); }
      else if (e.key === "0") { setZoom(1); setPan({ x: 0, y: 0 }); }
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(8, z * 1.25));
      else if (e.key === "-" || e.key === "_") setZoom((z) => Math.max(1, z / 1.25));
    };
    const onKeyUp = (e) => {
      if (e.key === " ") setSpaceHeld(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("keyup", onKeyUp);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("keyup", onKeyUp);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Wheel-to-zoom on the canvas wrapper. Centers around cursor.
  function onWheel(e) {
    if (loading) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => clamp(z * factor, 1, 8));
  }

  // Snapshot current canvas state (call BEFORE a stroke)
  function snapshot() {
    const ctx = ctxRef.current;
    const { w, h } = dimsRef.current;
    const data = ctx.getImageData(0, 0, w, h);
    setHistory((h) => {
      const next = [...h, data];
      // Cap stack to last 20 strokes to bound memory
      return next.length > 21 ? next.slice(next.length - 21) : next;
    });
  }

  function undo() {
    setHistory((stack) => {
      if (stack.length <= 1) return stack;
      const next = stack.slice(0, -1);
      const ctx = ctxRef.current;
      ctx.putImageData(next[next.length - 1], 0, 0);
      return next;
    });
  }

  function reset() {
    if (!cutoutImgRef.current) return;
    const ctx = ctxRef.current;
    const { w, h } = dimsRef.current;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(cutoutImgRef.current, 0, 0, w, h);
    setHistory([ctx.getImageData(0, 0, w, h)]);
  }

  // Convert client coords → canvas pixel coords. `getBoundingClientRect`
  // already reflects the transform (scale/translate) applied by the wrapper,
  // so a simple percentage map works at any zoom level.
  function clientToCanvas(clientX, clientY) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = (clientX - rect.left) / rect.width;
    const sy = (clientY - rect.top)  / rect.height;
    return { x: sx * canvas.width, y: sy * canvas.height };
  }

  function strokeAt(p) {
    const { w, h, scale } = dimsRef.current;
    const ctx = ctxRef.current;
    // size is display px on screen. canvas px = display px / displayScale.
    // factor in zoom too, so the brush keeps a consistent visual size as you
    // zoom: zoomed in 4x means each display px = 1/4 canvas px.
    const radius = size / scale / zoom;
    const last = lastPtRef.current;

    // Interpolate between last and current to avoid gaps when moving fast
    const points = [];
    if (last) {
      const dx = p.x - last.x;
      const dy = p.y - last.y;
      const dist = Math.hypot(dx, dy);
      const step = Math.max(1, radius * (1 - hardness * 0.5) * 0.4);
      const n = Math.max(1, Math.ceil(dist / step));
      for (let i = 1; i <= n; i++) {
        points.push({
          x: last.x + (dx * i) / n,
          y: last.y + (dy * i) / n,
        });
      }
    } else {
      points.push(p);
    }

    if (tool === "add") {
      // Paint IN: composite source-over of original photo, masked by a soft circle
      const orig = originalImgRef.current;
      const tmp = document.createElement("canvas");
      tmp.width = w; tmp.height = h;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(orig, 0, 0, w, h);
      // Build a mask from all the brush points
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = w; maskCanvas.height = h;
      const mctx = maskCanvas.getContext("2d");
      mctx.fillStyle = "white";
      for (const pt of points) {
        const grad = mctx.createRadialGradient(pt.x, pt.y, radius * hardness, pt.x, pt.y, radius);
        grad.addColorStop(0, "rgba(255,255,255,1)");
        grad.addColorStop(1, "rgba(255,255,255,0)");
        mctx.fillStyle = grad;
        mctx.beginPath();
        mctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        mctx.fill();
      }
      // Apply mask to the original-photo copy
      tctx.globalCompositeOperation = "destination-in";
      tctx.drawImage(maskCanvas, 0, 0);
      // Now composite the masked original onto the working canvas
      ctx.drawImage(tmp, 0, 0);
    } else {
      // Erase: clear alpha with a soft circle using destination-out
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      for (const pt of points) {
        const grad = ctx.createRadialGradient(pt.x, pt.y, radius * hardness, pt.x, pt.y, radius);
        grad.addColorStop(0, "rgba(0,0,0,1)");
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    lastPtRef.current = p;
  }

  // Track whether the current gesture is paint, pan, or nothing.
  // `mode` is set on pointerdown and read by pointermove/pointerup so we
  // don't paint and pan at the same time.
  const gestureRef = useRef({ kind: null, lastX: 0, lastY: 0 });

  function onPointerDown(e) {
    if (loading) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const wantsPan = panMode || spaceHeld || e.button === 1; // middle-click also pans
    if (wantsPan) {
      gestureRef.current = { kind: "pan", lastX: e.clientX, lastY: e.clientY };
    } else {
      gestureRef.current = { kind: "paint" };
      drawingRef.current = true;
      lastPtRef.current = null;
      snapshot();
      strokeAt(clientToCanvas(e.clientX, e.clientY));
    }
  }
  function onPointerMove(e) {
    const g = gestureRef.current;
    if (g.kind === "paint") {
      e.preventDefault();
      strokeAt(clientToCanvas(e.clientX, e.clientY));
    } else if (g.kind === "pan") {
      e.preventDefault();
      const dx = e.clientX - g.lastX;
      const dy = e.clientY - g.lastY;
      g.lastX = e.clientX;
      g.lastY = e.clientY;
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    }
  }
  function onPointerUp(e) {
    drawingRef.current = false;
    lastPtRef.current = null;
    gestureRef.current = { kind: null };
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  }

  async function confirm() {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      onConfirm?.(blob);
    } catch (err) {
      console.error("CutoutTouchUp export failed", err);
      alert("Couldn't save touch-up. Try again.");
      setBusy(false);
    }
  }

  // Portal to document.body so the modal escapes any parent stacking context
  // (e.g. the gallery's edge-nav buttons that were bleeding through earlier).
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(6,12,25,0.94)",
      display: "flex", flexDirection: "column",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    }}>
      {/* Header */}
      <div style={{
        padding: "0.85rem 1rem",
        borderBottom: `1px solid ${B.border}`,
        background: B.navy,
        display: "flex", alignItems: "center", gap: "0.85rem",
        flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: "180px" }}>
          <div style={{ color: B.gold, fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.02em" }}>🪄 Touch up cutout</div>
          <div style={{ color: B.muted, fontSize: "0.74rem", marginTop: "2px" }}>
            Paint to fix what the model missed. ⌘Z undo · [/] size · A/E switch · +/− zoom · Space pan
          </div>
        </div>
        <button onClick={onCancel} style={btnGhost}>Cancel</button>
      </div>

      {/* Mismatch warning — pre-fix-era cutouts had transparent borders trimmed,
          so the original photo no longer aligns 1:1. Add would scale wrong. */}
      {dimMismatch && (
        <div style={{
          padding: "0.55rem 1rem",
          background: "rgba(252,179,31,0.13)",
          borderBottom: `1px solid rgba(252,179,31,0.35)`,
          color: B.gold,
          fontSize: "0.78rem",
          lineHeight: 1.4,
        }}>
          <strong>⚠ Add disabled.</strong> This cutout was generated before the touch-up tool. Re-run the cutout first ("📐 Pre-crop & re-run cutout" or "📷 Replace photo") and Add will work correctly. Erase is fine to use now.
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        padding: "0.6rem 1rem",
        background: "#0a1730",
        borderBottom: `1px solid ${B.border}`,
        display: "flex", alignItems: "center", gap: "0.65rem",
        flexWrap: "wrap",
      }}>
        <button
          onClick={() => !dimMismatch && setTool("add")}
          disabled={dimMismatch}
          title={dimMismatch ? "Add disabled — cutout was generated before this tool. Re-run cutout first." : undefined}
          style={{ ...toolBtn(tool === "add" && !dimMismatch, B.green), opacity: dimMismatch ? 0.4 : 1, cursor: dimMismatch ? "not-allowed" : "pointer" }}
        >
          ➕ Add
        </button>
        <button onClick={() => setTool("erase")} style={toolBtn(tool === "erase", B.red)}>
          ➖ Erase
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", color: B.muted, fontSize: "0.78rem" }}>
          <span>Size</span>
          <input type="range" min={2} max={120} value={size} onChange={(e) => setSize(parseInt(e.target.value, 10))}
            style={{ width: "120px", accentColor: B.gold }} />
          <span style={{ width: "2.4ch", textAlign: "right", color: B.cream }}>{size}</span>
        </div>

        {/* Zoom controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <button onClick={() => setZoom((z) => Math.max(1, z / 1.25))} disabled={zoom <= 1}
            style={{ ...iconBtn, opacity: zoom <= 1 ? 0.4 : 1 }} title="Zoom out (−)">−</button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            style={{ ...iconBtn, minWidth: "3.5ch" }} title="Fit (0)">{Math.round(zoom * 100)}%</button>
          <button onClick={() => setZoom((z) => Math.min(8, z * 1.25))} disabled={zoom >= 8}
            style={{ ...iconBtn, opacity: zoom >= 8 ? 0.4 : 1 }} title="Zoom in (+)">+</button>
          <button onClick={() => setPanMode((p) => !p)}
            style={toolBtn(panMode, B.sky)} title="Drag to pan instead of paint">
            ✋ Pan
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", color: B.muted, fontSize: "0.78rem" }}>
          <span>Edge</span>
          <input type="range" min={0} max={100} value={Math.round(hardness * 100)} onChange={(e) => setHardness(parseInt(e.target.value, 10) / 100)}
            style={{ width: "100px", accentColor: B.gold }} />
        </div>

        <div style={{ flex: 1 }} />
        <button onClick={undo} disabled={history.length <= 1} style={{ ...btnGhost, opacity: history.length <= 1 ? 0.4 : 1 }}>↶ Undo</button>
        <button onClick={reset} style={btnGhost}>↺ Reset</button>
      </div>

      {/* Stage — wheel-to-zoom on the wrapper, transform applied to the
          canvas so pan/zoom gestures don't repaint the alpha mask. */}
      <div ref={wrapperRef} onWheel={onWheel} style={{
        flex: 1, position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem", overflow: "hidden",
        touchAction: "none",
        backgroundImage: `
          linear-gradient(45deg, rgba(255,255,255,0.04) 25%, transparent 25%),
          linear-gradient(-45deg, rgba(255,255,255,0.04) 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.04) 75%),
          linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.04) 75%)
        `,
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
      }}>
        {loading && <div style={{ color: B.muted }}>Loading…</div>}
        {error && <div style={{ color: "#ff8a8a" }}>{error}</div>}
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{
            cursor: (panMode || spaceHeld)
              ? "grab"
              : (tool === "add" ? "crosshair" : "cell"),
            touchAction: "none",
            display: loading || error ? "none" : "block",
            boxShadow: "0 14px 48px rgba(0,0,0,0.55)",
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "center center",
            transition: gestureRef.current?.kind ? "none" : "transform 0.1s ease",
          }}
        />
      </div>

      {/* Footer */}
      <div style={{
        padding: "0.85rem 1rem",
        borderTop: `1px solid ${B.border}`,
        background: B.navy,
        display: "flex", alignItems: "center", gap: "0.6rem",
      }}>
        <div style={{ flex: 1, color: B.muted, fontSize: "0.74rem" }}>
          {tool === "add"
            ? "Add: paints the original photo back in (great for restoring lost dark areas)."
            : "Erase: removes pixels from the cutout (great for trimming stray noise)."}
        </div>
        <button onClick={confirm} disabled={busy || loading} style={{
          background: busy ? "rgba(112,190,68,0.4)" : B.green,
          color: "white", border: "none", borderRadius: "10px",
          padding: "0.6rem 1.1rem", fontWeight: 800, fontSize: "0.9rem",
          cursor: busy || loading ? "default" : "pointer",
          fontFamily: "inherit",
        }}>
          {busy ? "Saving…" : "✓ Save touch-up"}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

// Load via fetch → blob → blob: URL. Sidesteps CORS issues with
// crossOrigin="anonymous" against Storage URLs that may not return CORS
// headers consistently. Blob URLs are same-origin, so canvas exports cleanly.
async function loadImage(src) {
  let resolvedSrc = src;
  if (!src.startsWith("blob:") && !src.startsWith("data:")) {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Image fetch failed (${res.status})`);
    const blob = await res.blob();
    resolvedSrc = URL.createObjectURL(blob);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error("Image decode failed"));
    img.src = resolvedSrc;
  });
}

function toolBtn(active, accent) {
  return {
    background: active ? `${accent}28` : "transparent",
    color: active ? accent : "rgba(255,218,173,0.65)",
    border: `1px solid ${active ? accent : "rgba(255,218,173,0.22)"}`,
    borderRadius: "999px",
    padding: "0.4rem 0.95rem",
    fontSize: "0.85rem",
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
  };
}

const btnGhost = {
  background: "transparent",
  color: "rgba(255,218,173,0.6)",
  border: "1px solid rgba(255,218,173,0.22)",
  borderRadius: "8px",
  padding: "0.4rem 0.85rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const iconBtn = {
  background: "transparent",
  color: "rgba(255,218,173,0.7)",
  border: "1px solid rgba(255,218,173,0.22)",
  borderRadius: "6px",
  padding: "0.32rem 0.6rem",
  fontSize: "0.85rem",
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  minWidth: "2ch",
  textAlign: "center",
};

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}
